import React, { useState } from 'react';
import { Button, Row, Col, Typography, Input, Tabs } from 'antd';
import ResponsiveModal from 'components/shared-components/ResponsiveModal';
import {
  PlusOutlined,
  FontSizeOutlined,
  FileTextOutlined,
  PictureOutlined,
  EditOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  DownSquareOutlined,
  SafetyCertificateOutlined,
  PlayCircleOutlined,
  UnorderedListOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { ADD_CONTENT_MENU, DYNAMIC_DATA_COMPONENT_TYPES } from 'utils/metaFlowComponents';

const { Text, Title } = Typography;

const ICON_MAP = {
  text_large_heading: <FontSizeOutlined style={{ fontSize: '16px', color: '#1890ff' }} />,
  text_small_heading: <FontSizeOutlined style={{ fontSize: '14px', color: '#1890ff' }} />,
  text_body: <FileTextOutlined style={{ fontSize: '15px', color: '#1890ff' }} />,
  text_caption: <FileTextOutlined style={{ fontSize: '12px', color: '#1890ff' }} />,
  image: <PictureOutlined style={{ fontSize: '16px', color: '#52c41a' }} />,
  text_input: <EditOutlined style={{ fontSize: '16px', color: '#722ed1' }} />,
  text_area: <EditOutlined style={{ fontSize: '16px', color: '#722ed1' }} />,
  date_picker: <CalendarOutlined style={{ fontSize: '16px', color: '#722ed1' }} />,
  navigation_list: <UnorderedListOutlined style={{ fontSize: '16px', color: '#fa8c16' }} />,
  radio: <CheckCircleOutlined style={{ fontSize: '16px', color: '#fa8c16' }} />,
  checkbox: <CheckSquareOutlined style={{ fontSize: '16px', color: '#fa8c16' }} />,
  dropdown: <DownSquareOutlined style={{ fontSize: '16px', color: '#fa8c16' }} />,
  opt_in: <SafetyCertificateOutlined style={{ fontSize: '16px', color: '#fa8c16' }} />,
  button: <PlayCircleOutlined style={{ fontSize: '16px', color: '#eb2f96' }} />,
};

const BG_MAP = {
  text_large_heading: '#e6f7ff',
  text_small_heading: '#e6f7ff',
  text_body: '#e6f7ff',
  text_caption: '#e6f7ff',
  image: '#f6ffed',
  text_input: '#f9f0ff',
  text_area: '#f9f0ff',
  date_picker: '#f9f0ff',
  navigation_list: '#fff7e6',
  radio: '#fff7e6',
  checkbox: '#fff7e6',
  dropdown: '#fff7e6',
  opt_in: '#fff7e6',
  button: '#fff0f6',
};

const COMPONENT_DESCRIPTIONS = {
  text_large_heading: 'Large bold header text',
  text_small_heading: 'Medium weight header text',
  text_body: 'Standard paragraph text',
  text_caption: 'Small helper or description text',
  image: 'Upload or link an image',
  text_input: 'Single line text input field',
  text_area: 'Multi-line detailed text input',
  date_picker: 'Standard date selection field',
  navigation_list: 'List with image, title and tap action',
  radio: 'Select one option from a list',
  checkbox: 'Select multiple options from a list',
  dropdown: 'Select an option from a dropdown menu',
  opt_in: 'Checkbox for terms or agreements',
  button: 'Action button or footer button',
};

const AddContentMenu = ({
  onAdd,
  disabled,
  hidden = false,
  disabledTypes = [],
  buttonLabel = 'Add content',
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  if (hidden) {
    return null;
  }

  const handleOpen = () => {
    setOpen(true);
    setSearchQuery('');
    setActiveTab('all');
  };
  const handleClose = () => setOpen(false);

  const handleSelect = (type) => {
    if (disabledTypes.includes(type)) {
      return;
    }
    onAdd(type);
    handleClose();
  };

  const filteredMenu = ADD_CONTENT_MENU.map((group) => {
    const matchedChildren = group.children.filter((item) => {
      const matchesSearch =
        item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (COMPONENT_DESCRIPTIONS[item.type] || '')
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesTab =
        activeTab === 'all' || DYNAMIC_DATA_COMPONENT_TYPES.includes(item.type);

      return matchesSearch && matchesTab;
    });
    return {
      ...group,
      children: matchedChildren,
    };
  }).filter((group) => group.children.length > 0);

  return (
    <>
      <style>{`
        .add-content-item-card {
          transition: all 0.2s ease-in-out;
        }
        .add-content-item-card:hover {
          border-color: #1890ff !important;
          background-color: #fafafa !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }
      `}</style>

      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        disabled={disabled}
        onClick={handleOpen}
        style={{
          height: '40px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: 500,
        }}
      >
        {buttonLabel}
      </Button>

      <ResponsiveModal
        title={'Components'}
        extra={
          <Input
            placeholder="Search components..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            style={{ borderRadius: '6px' }}
          />
        }
        open={open}
        onClose={handleClose}
        footer={null}
        width={720}
        centered
        headerStyle={{ padding: '4px' }}
        bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: '0 24px' }}
        destroyOnClose
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'all', label: 'All' },
            { key: 'dynamic', label: 'Dynamic (Support Data From API)' },
          ]}
        />
        {filteredMenu.length > 0 ? (
          filteredMenu.map((group) => (
            <div key={group.key} style={{ marginBottom: '24px' }}>
              <Text
                strong
                style={{
                  display: 'block',
                  marginBottom: '12px',
                  fontSize: '12px',
                  color: '#8c8c8c',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  fontWeight: 600,
                }}
              >
                {group.label}
              </Text>
              <Row gutter={[12, 12]}>
                {group.children.map((item) => {
                  const isDisabled = disabledTypes.includes(item.type);
                  return (
                    <Col key={item.type} xs={24} sm={12}>
                      <div
                        className={isDisabled ? undefined : 'add-content-item-card'}
                        onClick={() => handleSelect(item.type)}
                        style={{
                          padding: '12px 16px',
                          border: '1px solid #f0f0f0',
                          borderRadius: '8px',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          background: isDisabled ? '#fafafa' : '#fff',
                          height: '100%',
                          opacity: isDisabled ? 0.55 : 1,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            background: BG_MAP[item.type] || '#f5f5f5',
                            flexShrink: 0,
                          }}
                        >
                          {ICON_MAP[item.type]}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <Text strong style={{ fontSize: '14px', color: '#262626' }}>
                            {item.label}
                            {isDisabled ? ' (unavailable)' : ''}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                            {isDisabled
                              ? 'Remove other dynamic fields first — navigation list must be alone on the screen'
                              : COMPONENT_DESCRIPTIONS[item.type] || ''}
                          </Text>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>
            No components match "{searchQuery}"
          </div>
        )}
      </ResponsiveModal>
    </>
  );
};

export default AddContentMenu;
